import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { orgService, vehicleService, teamService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { StatItem } from "@/components/common/Telemetry";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Mail, Shield, Building2, LogOut } from "lucide-react";

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState(null);
  const [counts, setCounts] = useState({ vehicles: null, facilities: null, team: null });

  useEffect(() => {
    orgService.get().then((o) => setOrgName(o.name)).catch(() => {});
    Promise.all([
      vehicleService.list().catch(() => []),
      orgService.facilities().catch(() => []),
      teamService.list().catch(() => []),
    ]).then(([vehicles, facilities, team]) => {
      setCounts({ vehicles: vehicles.length, facilities: facilities.length, team: team.length });
    });
  }, []);

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader title="Profile" subtitle="Your account details and preferences." />
      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card className="border-border/70 bg-card/60 p-6 text-center">
          <Avatar className="mx-auto h-20 w-20 border border-border/60"><AvatarFallback className="bg-secondary text-2xl font-semibold">{user?.initials}</AvatarFallback></Avatar>
          <h2 className="mt-4 font-display text-xl font-semibold">{user?.name}</h2>
          <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-medium text-primary"><Shield className="h-3 w-3" /> {user?.role}</div>
          <div className="mt-5 space-y-2 text-left text-sm">
            <div className="flex items-center gap-2 text-muted-foreground"><Mail className="h-4 w-4" /> {user?.email}</div>
            <div className="flex items-center gap-2 text-muted-foreground"><Building2 className="h-4 w-4" /> {orgName || "…"}</div>
          </div>
          <Button variant="secondary" className="mt-6 w-full text-red-400 hover:text-red-400" onClick={() => { logout(); navigate("/login"); }} data-testid="profile-logout"><LogOut className="mr-1.5 h-4 w-4" /> Sign out</Button>
        </Card>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <StatItem label="Vehicles managed" value={counts.vehicles ?? "—"} />
            <StatItem label="Facilities" value={counts.facilities ?? "—"} />
            <StatItem label="Team members" value={counts.team ?? "—"} />
          </div>
          <Card className="border-border/70 bg-card/60 p-6">
            <h3 className="mb-5 font-display text-lg font-medium">Account details</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-1.5"><span className="text-sm font-medium">Full name</span><Input defaultValue={user?.name} disabled /></label>
              <label className="space-y-1.5"><span className="text-sm font-medium">Email</span><Input defaultValue={user?.email} disabled /></label>
              <label className="space-y-1.5"><span className="text-sm font-medium">Role</span><Input defaultValue={user?.role} disabled /></label>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Self-service profile editing isn't available yet — an admin can update your role from the Team page.
              To change your password, go to <button className="text-primary hover:underline" onClick={() => navigate("/settings")}>Settings → Security</button>.
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
